import { ArrowUpRight, MessageCircle } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { useLang, WHATSAPP_LINK, HAS_WHATSAPP } from "../contexts/LanguageContext";
import { LineReveal } from "./motion/Reveal";

export default function Hero({ onCTAClick }) {
  const { t, isRTL } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  // Slow subtle parallax on the hero image and headline
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.06]);
  const copyY = useTransform(scrollYProgress, [0, 1], ["0%", "-8%"]);

  return (
    <section
      ref={ref}
      id="top"
      className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden"
      data-testid="hero"
    >
      {/* Decorative background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-cb-cream" />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.6, delay: 0.2 }}
          className="absolute top-0 end-0 w-[55%] h-full opacity-[0.06]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 80% 20%, #0A1128 0, transparent 60%)",
          }}
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, delay: 0.4 }}
          className="absolute bottom-0 start-0 w-96 h-96 rounded-full"
          style={{
            background:
              "radial-gradient(closest-side, rgba(212,175,55,0.22), transparent)",
          }}
        />
      </div>

      <div className="container-cb grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <motion.div style={{ y: copyY }} className="lg:col-span-7">
          {/* Overline with a drawing gold divider */}
          <div className="mb-6 flex items-center gap-3 text-[11px] tracking-[0.35em] uppercase text-cb-gold">
            <motion.span
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              style={{ transformOrigin: isRTL ? "right" : "left" }}
              className="block w-10 h-px bg-cb-gold"
            />
            <motion.span
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.35 }}
            >
              {t.hero.overline}
            </motion.span>
          </div>

          {/* Serif kinetic headline: line-by-line masked reveal */}
          <h1
            className="text-4xl sm:text-5xl lg:text-[64px] leading-[1.02] text-cb-primary"
            style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
          >
            <LineReveal text={t.hero.title} stagger={0.055} delay={0.15} />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6 text-lg text-cb-secondary max-w-2xl leading-relaxed"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 flex flex-wrap gap-3"
          >
            <button
              data-testid="hero-cta-primary"
              onClick={onCTAClick}
              className="btn-primary group inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-medium relative overflow-hidden"
            >
              <span className="relative z-10">{t.hero.cta1}</span>
              <ArrowUpRight size={18} className={`relative z-10 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 ${isRTL ? "rotate-90" : ""}`} />
            </button>
            {HAS_WHATSAPP && (
              <a
                data-testid="hero-cta-wa"
                href={WHATSAPP_LINK("Hi Capital Bay, I'd like to book a discovery call.")}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-medium border border-cb-primary/15 text-cb-primary hover:bg-cb-primary hover:text-white transition-colors"
              >
                <MessageCircle size={18} />
                {t.hero.cta2}
              </a>
            )}
          </motion.div>

          <motion.div
            initial="hidden"
            animate="show"
            variants={{
              hidden: {},
              show: { transition: { staggerChildren: 0.09, delayChildren: 1.05 } },
            }}
            className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6"
          >
            {[
              [t.hero.stat1, t.hero.stat1l],
              [t.hero.stat2, t.hero.stat2l],
              [t.hero.stat3, t.hero.stat3l],
              [t.hero.stat4, t.hero.stat4l],
            ].map(([n, l], i) => (
              <motion.div
                key={i}
                variants={{
                  hidden: { opacity: 0, y: 18 },
                  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
                }}
                className="border-s-2 border-cb-gold ps-3"
                data-testid={`hero-stat-${i}`}
              >
                <div className="text-2xl lg:text-3xl font-semibold text-cb-primary" style={{ fontFamily: "Outfit" }}>
                  {n}
                </div>
                <div className="text-xs uppercase tracking-widest text-cb-secondary mt-1">{l}</div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Hero image with parallax + editorial spotlight framing */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5"
        >
          <div className="relative">
            {/* clipped editorial frame */}
            <div className="relative rounded-[28px] overflow-hidden border border-cb-primary/10 shadow-[0_30px_80px_-30px_rgba(10,17,40,0.35)] aspect-[4/5]">
              <motion.img
                src="/hero-riyadh.jpg"
                alt="Riyadh skyline mid-morning: KAFD, Al Majdoul Tower and Kingdom Centre Tower under a clear blue sky"
                className="w-full h-full object-cover"
                style={{ y: imgY, scale: imgScale }}
              />
              {/* spotlight vignette */}
              <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(120% 80% at 50% 30%, transparent 55%, rgba(10,17,40,0.55) 100%)" }} />
              <div className="absolute inset-0 bg-gradient-to-t from-cb-dark/60 via-transparent to-transparent" />

              {/* caption card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 1.1 }}
                className="absolute bottom-6 start-6 end-6"
              >
                <div className="glass rounded-xl p-4 border border-white/30 backdrop-blur-md">
                  <div className="text-[10px] tracking-[0.35em] text-cb-emerald font-semibold uppercase">Riyadh · KSA</div>
                  <div className="text-cb-primary font-medium mt-1" style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic" }}>
                    The Kingdom's financial heart — where we underwrite the numbers.
                  </div>
                </div>
              </motion.div>

              {/* corner tick marks — editorial detail */}
              <div className="absolute top-4 start-4 w-6 h-6 border-t border-s border-white/70" />
              <div className="absolute top-4 end-4 w-6 h-6 border-t border-e border-white/70" />
              <div className="absolute bottom-4 start-4 w-6 h-6 border-b border-s border-white/70" />
              <div className="absolute bottom-4 end-4 w-6 h-6 border-b border-e border-white/70" />
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.7, rotate: -8 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 0.9, delay: 1.15, ease: [0.16, 1, 0.3, 1] }}
              className="absolute -top-6 -end-4 bg-cb-gold text-cb-primary px-4 py-2 rounded-full text-xs uppercase tracking-widest font-semibold shadow-lg"
            >
              Vision 2030
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 1.4 }}
        className="hidden md:flex absolute bottom-6 start-1/2 -translate-x-1/2 flex-col items-center gap-2 text-[10px] tracking-[0.35em] uppercase text-cb-secondary"
      >
        <span>Scroll</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="block w-px h-8 bg-cb-primary/30"
        />
      </motion.div>
    </section>
  );
}
